import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Header, Divider, TextArea, Button } from 'components/patterns'
import { Icon__Alert as IconAlert } from 'components/patterns/Icon/components'
import strings from 'utils/strings'
import { createSystemNotification } from 'actions/application'
import {
  PTO_TEMPLATE_SAVE_FAILED,
  PTO_TEMPLATE_SAVE_SUCCESS,
  savePtoTemplate,
} from 'actions/powerTakeOff'
import { getSelectedTemplateChanges } from 'selectors/powerTakeOff'
import {
  NOTIFICATION_ERROR,
  NOTIFICATION_SUCCESS,
  PTO_TEMPLATE,
  PTO_TEMPLATE_COMMIT,
} from 'utils/constants'
import { useSelector } from 'hooks/redux'
import {
  removeComponentMeta,
  setIsEditing,
  setSelectedIdWithDrawer,
} from 'actions/navigator'
import { yup } from 'schemas/validation'
import { getIsEditing } from 'selectors/navigator'

function getEditedAddedRemovedFragmentsForDomain(domainId, _, changes = {}) {
  const changedFragmentsForDomain = Object.values(changes).filter(
    ({ domain }) => domain === domainId
  )

  const added = changedFragmentsForDomain.filter(
    ({ status }) => status === 'added'
  )

  const removed = changedFragmentsForDomain.filter(
    ({ status }) => status === 'removed'
  )

  const edited = changedFragmentsForDomain.filter(
    ({ oldValue, newValue }) => oldValue !== newValue
  )

  return {
    edited,
    added,
    removed,
  }
}

export default function Commit({
  template: { templateId, fragments, domains, name, description },
}) {
  const dispatch = useDispatch()
  const [notes, updateNotes] = useState('')
  const [saving, updateSaving] = useState(false)
  const changes = useSelector(getSelectedTemplateChanges, { templateId }) || []
  const isEditing = useSelector(getIsEditing)

  const editedDomains = new Set(
    Object.values(changes).map((fragment) => domains[fragment.domain])
  )

  const edits = [...editedDomains].map((domain) => ({
    domain: domain.name,
    ...getEditedAddedRemovedFragmentsForDomain(domain.id, fragments, changes),
  }))

  const handleCancel = () => {
    dispatch(removeComponentMeta([PTO_TEMPLATE_COMMIT]))
    dispatch(setSelectedIdWithDrawer(null))
  }

  const handleSave = () => {
    updateSaving(true)
    dispatch(
      savePtoTemplate({ templateId, name, description, notes, fragments })
    )
      .then((result) => {
        // Guard against de-dup: if an identical save was already in-flight,
        // promiseApiAction resolves with undefined. Running the cleanup
        // dispatches against a save that may not have committed would leave
        // the UI in an inconsistent state.
        if (!result) return
        dispatch(removeComponentMeta([PTO_TEMPLATE_COMMIT]))
        dispatch(setSelectedIdWithDrawer(null))
        dispatch(setIsEditing(!isEditing, PTO_TEMPLATE))
        createSystemNotification({
          notificationMessage: strings.pto_template_update_succeeded,
          notificationId: PTO_TEMPLATE_SAVE_SUCCESS,
          notificationType: NOTIFICATION_SUCCESS,
          persistent: true,
        })
      })
      .catch(() => {
        dispatch(removeComponentMeta([PTO_TEMPLATE_COMMIT]))
        dispatch(setSelectedIdWithDrawer(null))
        dispatch(
          createSystemNotification({
            notificationMessage: strings.pto_template_update_failed,
            notificationId: PTO_TEMPLATE_SAVE_FAILED,
            notificationType: NOTIFICATION_ERROR,
            persistent: true,
          })
        )
      })
  }

  return (
    <CommitContainer>
      <Header type="h3">
        <IconAlert /> {strings.pto_template_edit_template}
      </Header>
      <div>
        {edits.length > 0 &&
          edits
            // sort by domain name
            .sort((a, b) => `${a.domain}`.localeCompare(`${b.domain}`))
            .map(({ domain, added, removed, edited }) => (
              <div key={domain}>
                <strong>{domain}</strong>
                <br />
                {edited.length > 0 && (
                  <>
                    <strong>Updated the following settings:</strong>
                    <ul>
                      {edited.map(({ name }) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </>
                )}

                {added.length > 0 && (
                  <>
                    <strong>Added the following settings:</strong>
                    <ul>
                      {added.map(({ name }) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </>
                )}

                {removed.length > 0 && (
                  <>
                    <strong>Removed the following settings:</strong>
                    <ul>
                      {removed.map(({ name }) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
      </div>
      <CommitForm>
        <TextArea
          value={notes}
          onChange={updateNotes}
          width="100%"
          label={strings.notes}
          rules={yup.string().required().max(1000)}
          maxLength={1000}
          placeholder={strings.notes_required}
          autosize={{
            minRows: 6,
            maxRows: 8,
          }}
          data-testid="notes-textarea"
        />
        <StyledDivider />
        <Button onClick={handleCancel} type="secondary" disabled={saving}>
          {strings.cancel}
        </Button>
        <Button
          onClick={handleSave}
          type="primary"
          disabled={notes.length === 0 || saving}
          loading={saving}
          style={{ float: 'right' }}
        >
          {strings.save}
        </Button>
      </CommitForm>
    </CommitContainer>
  )
}

Commit.propTypes = {
  template: PropTypes.shape({
    templateId: PropTypes.string,
    fragments: PropTypes.array,
    domains: PropTypes.array,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
}

const CommitContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  overflow: auto;
  margin: -20px;
  padding: 20px;
`

const CommitForm = styled.div`
  align-self: flex-end;
  width: 100%;
  margin-top: auto;
`

const StyledDivider = styled(Divider)`
  margin-left: -20px;
  width: calc(100% + 40px);
`
